import { useIsAuthenticated } from "@azure/msal-react"
import { Immutable, ImmutableObject } from "@hookstate/core"
import StarIcon from "@mui/icons-material/Star"
import { Alert } from "@mui/material"
// import { differenceInDays } from "date-fns"
import React, { FC } from "react"

import { AdvancedForms } from "src/components/common/advancedForm"
import { AdvancedFormFieldDependency } from "src/components/common/advancedForm/AdvancedFormField"
import { Button } from "src/components/common/button"

import { create } from "src/helpers/bem"
import { deepClone } from "src/helpers/deepClone"
import { calculatePrice, formatPrice } from "src/helpers/price"

import { useBookings } from "src/states/bookings"
import { useLocale } from "src/states/locale"
import {
  CartProduct,
  getCartProductAmount,
  useCart,
  useCarts,
} from "src/states/shop/carts"
import { mergeProductAttributes } from "src/states/shop/productAttributes"
import { Product } from "src/states/shop/products"
import {
  ProductVariant,
  useProductVariants,
} from "src/states/shop/productVariants"

import { TranslationMessages } from "src/translations"
import { xRMApiSalesorderPhase } from "src/types/xRM"

import styles from "./ShopDetailHeaderContent.module.scss"

const bem = create(styles, "ShopDetailHeaderContent")

export type ShopDetailHeaderContentProps = {
  product: ImmutableObject<Product>
  productVariant: ImmutableObject<ProductVariant>
  setProductVariant: React.Dispatch<
    React.SetStateAction<ImmutableObject<ProductVariant>>
  >
  messages: TranslationMessages
}

export const ShopDetailHeaderContent: FC<ShopDetailHeaderContentProps> = ({
  product,
  productVariant,
  setProductVariant,
  messages,
}) => {
  const m = messages.pages.shop.detail.header.content
  const locale = useLocale()
  const isAuthenticated = useIsAuthenticated()
  const { variants } = useProductVariants()
  const { currentCartId } = useCarts()
  const cart = useCart()

  const { bookings } = useBookings()
  const currentBooking = bookings?.find((b) => b.id === cart.id)
  const bookingAssignedSize = currentBooking?.assignedStandSize
  const exhibitionDuration = currentBooking ? currentBooking.duration : 0

  const productVariantAlreadyInCart = cart.products.find(
    (cartProduct) => cartProduct.productVariant?.id === productVariant.id,
  )

  const displayVariants =
    productVariant.isOptional !== true &&
    variants &&
    (variants.length > 1 ||
      (variants.length === 1 && productVariant?.id !== product?.id))

  const productWithUpdatedPrice = {
    ...product,
    price: calculatePrice({
      calculationMethod: product.productCalculation,
      price: product.price ?? 0,
      duration: exhibitionDuration,
      assignedStandSize: bookingAssignedSize,
    }),
  }

  const productVariantWithUpdatedPrice = {
    ...productVariant,
    price: calculatePrice({
      calculationMethod: productVariant.productCalculation,
      price: productVariant?.price ?? 0,
      duration: exhibitionDuration,
      assignedStandSize: bookingAssignedSize,
    }),
  }

  const handleAddToCart = () => {
    mergeProductAttributes({
      open: true,
      addNew: true,
      cartProductId: undefined,
      product: deepClone<Product>(
        productVariant.isOptional
          ? productVariantWithUpdatedPrice
          : productWithUpdatedPrice,
      ),
      productVariant: deepClone<Immutable<ProductVariant>>(
        productVariantWithUpdatedPrice,
      ),
      form: [deepClone<AdvancedForms>(productVariant.productAttributesForm)],
      dependencies: deepClone<Immutable<AdvancedFormFieldDependency>[]>(
        productVariant.productAttributeDependencies,
      ),
    })
  }

  const allowedPhases: xRMApiSalesorderPhase[] = ["Invoicing", "Service"]
  const isBookingInAllowedPhase =
    !!currentBooking && allowedPhases.includes(currentBooking.phase)

  const isCartInactive =
    !currentCartId || currentCartId === "0" || !isBookingInAllowedPhase
  const isMaxAmountReached =
    getCartProductAmount(
      productVariantAlreadyInCart as ImmutableObject<CartProduct>,
    ) >=
    (productVariantAlreadyInCart?.productVariant?.maximumOrderAmount ?? 1000000)
  const buttonDisabled = isCartInactive || isMaxAmountReached
  const buttonTitle = productVariant.productAttributesForm?.fields?.some(
    (pa) => pa.field === "Amount",
  )
    ? productVariantAlreadyInCart
      ? m.button.titleAdd
      : m.button.title
    : m.button.titleNoAdd
  const isWebShopEnabled = process.env.ENABLE_WEBSHOP === "true"

  return (
    <div className={bem()}>
      <div className={bem("content")}>
        {product?.tag && <p className={bem("product__tag")}>{product.tag}</p>}
        {product.name && productVariant.name && (
          <>
            {product.hasVariants && (
              <h6 className={bem("content__title")}>{product.name}</h6>
            )}
            <h1 className={bem("content__title")}>{productVariant.name}</h1>
          </>
        )}
        {productVariant?.note && (
          <div className={bem("product__note")}>{productVariant.note}</div>
        )}
      </div>
      {productVariant?.price != null && (
        <div className={bem("price")}>
          <div className={bem("price__wrapper")}>
            <div className={bem("price__value")}>
              {currentBooking !== undefined ||
              (!currentBooking &&
                productVariant.productCalculation !== "Charges")
                ? formatPrice(productVariantWithUpdatedPrice.price, locale)
                : "-"}
            </div>
            {productVariant.productCalculation !== "Charges" &&
              productVariant?.unit && (
                <div className={bem("price__unit")}>
                  &nbsp;/&nbsp;{productVariant?.unit}
                </div>
              )}
          </div>
        </div>
      )}
      <div className={bem("price")}>
        {productVariant?.priceLabel && (
          <div className={bem("price__label")}>{productVariant.priceLabel}</div>
        )}
        {productVariant?.priceInfo && (
          <div className={bem("price__info")}>{productVariant.priceInfo}</div>
        )}
      </div>
      {displayVariants ? (
        <div className={bem("variants")}>
          {variants.map((variant, i) => (
            <button
              key={`product-variant-${i}`}
              className={bem("variant", {
                "is-active": productVariant.id === variants[i].id,
              })}
              onClick={() => setProductVariant(variants[i])}
            >
              {variant?.favorite ? (
                <StarIcon className={bem("variant__favorite")} />
              ) : null}
              <div className={bem("variant__title")}>{variant?.name}</div>
              <div className={bem("variant__price")}>
                {/* ToDo: add calculation function */}
                {formatPrice(variant.price, locale)}{" "}
                {variant?.unit && (
                  <i className={bem("variant__price__unit")}>
                    / {variant.unit}
                  </i>
                )}
              </div>
            </button>
          ))}
        </div>
      ) : null}

      <div className={bem("price")}>
        {productVariant.mandatoryProducts &&
          productVariant.mandatoryProducts.length > 0 && (
            <>
              <div className={bem("price__label")}>
                {m.mandatoryProductLabel}
              </div>
              <div className={bem("price__mandatory")}>
                {productVariant.mandatoryProducts.map((p, index) =>
                  p.quantity != null ? (
                    <div key={index}>
                      {p.quantity} x {p.name}
                    </div>
                  ) : (
                    <div key={index}>{p.name}</div>
                  ),
                )}
              </div>
            </>
          )}
      </div>
      {isWebShopEnabled && (product?.webshopEnabled ?? true) && (
        <div className={bem("button")}>
          <Button
            buttonType="primary"
            disabled={buttonDisabled}
            title={buttonTitle}
            variant="outlined"
            onClick={handleAddToCart}
          />
          {buttonDisabled && (
            <Alert
              className={bem("button__helper")}
              severity="info"
              variant="outlined"
            >
              {isAuthenticated
                ? isCartInactive
                  ? m.button.helper
                  : isMaxAmountReached
                    ? m.button.maxAmountReached
                    : ""
                : m.button.helper1}
            </Alert>
          )}
        </div>
      )}
    </div>
  )
}
